// Identity material loader.
//
// Desktop identities are derived from the OS vault in memory; browser and
// not-yet-migrated legacy identities use non-exportable IndexedDB keys.
// Both are loaded once per session. The cached `IdentityKeyMaterial` is
// then reused by every E2E send / receive without going back to
// the keystore.
//
// We deliberately keep this OUT of the React tree as a
// module-scoped cache: every hook and helper that needs the key
// shares the same in-memory copy, and a `clear()` call at logout
// time wipes it. The keystore itself is still the durable home —
// this is just a process-lifetime mirror.
//
// Privacy boundary (Req 4.6): this module imports from
// `@link/storage` and `@link/crypto`, which the ESLint boundary
// allows for `apps/web`. It MUST NOT be imported by `apps/api`.

import {
  deriveIdentityKey,
  signChallenge,
  type IdentityKeyMaterial,
} from "@link/crypto";
import { createWebKeyStore } from "@link/storage";

import { readSessionToken } from "./http";
import { devicePhraseForAccount, isNativeVault } from "./device-vault";
import { mnemonicToSeed } from "@link/crypto";

let cached: IdentityKeyMaterial | null = null;
let cachedAccountXid: string | null = null;

/**
 * Load the local user's identity material. Returns `null` when:
 *
 *   - No session token is present.
 *   - The keystore has no row for this account (user hasn't
 *     completed signup on this device).
 *
 * Callers must handle the `null` case — every E2E send / receive
 * path needs the key, so a `null` result is a hard error for those
 * surfaces. Signed-out paths skip the cipher work entirely.
 */
export async function loadIdentity(
  accountXid: string,
): Promise<IdentityKeyMaterial | null> {
  const normalizedAccountXid = accountXid.trim();
  if (normalizedAccountXid.length === 0) {
    throw new Error("Local identity lookup requires the signed-in Account ID.");
  }
  if (cached !== null && cachedAccountXid === normalizedAccountXid) {
    return cached;
  }
  if (readSessionToken() === null) return null;

  if (isNativeVault()) {
    const phrase = await devicePhraseForAccount(normalizedAccountXid);
    if (phrase) {
      const seed = await mnemonicToSeed(phrase);
      try { cached = await deriveIdentityKey(seed); cachedAccountXid = normalizedAccountXid; return cached; }
      finally { seed.fill(0); }
    }
    // Legacy desktop identities remain usable until explicitly migrated by
    // recovery/passkey. Non-extractable legacy keys cannot reconstruct a seed.
  }

  const store = createWebKeyStore();
  const stored = await store.get(normalizedAccountXid);
  const storedEcdh = await store.get(`${normalizedAccountXid}:ecdh`);
  if (stored === null || storedEcdh === null || isCryptoKeyPair(storedEcdh)) return null;

  // The keystore returns the persisted CryptoKey shape. We need an
  // `IdentityKeyMaterial` (with raw + hash). Re-derive those from
  // the stored seed if available, or import directly.
  if (isCryptoKeyPair(stored)) {
    // Recover the raw public key + hash from the stored CryptoKey.
    // This branch fires when signup ran on this device and the
    // keystore has a CryptoKeyPair (the native path).
    const subtle = globalThis.crypto.subtle;
    const publicKeyRaw = new Uint8Array(
      await subtle.exportKey("raw", stored.publicKey),
    );
    const publicKeyHash = new Uint8Array(
      await subtle.digest("SHA-256", publicKeyRaw),
    );
    cached = {
      privateKey: stored.privateKey,
      ecdhPrivateKey: storedEcdh,
      publicKey: stored.publicKey,
      publicKeyRaw,
      publicKeyHash,
    };
    cachedAccountXid = normalizedAccountXid;
    return cached;
  }

  // The fallback shape (`CryptoKey` alone, JWK fallback path) is
  // only used on browsers that don't support structured-cloning
  // CryptoKey into IndexedDB — a vanishingly small population. We
  // can't recover the public key from a private-only handle, so
  // we surface null and the auth flow re-runs.
  return null;
}

function isCryptoKeyPair(
  value: CryptoKey | CryptoKeyPair,
): value is CryptoKeyPair {
  return (
    typeof value === "object" &&
    value !== null &&
    "privateKey" in value &&
    "publicKey" in value
  );
}

/** Clear the in-memory mirror. Called from sign-out. */
export function clearIdentityCache(): void {
  cached = null;
  cachedAccountXid = null;
}

/**
 * Re-derive an `IdentityKeyMaterial` from a fresh mnemonic seed.
 * Used by the auth ceremony so the in-memory cache is populated
 * the moment signup or login completes.
 */
export async function primeIdentityFromSeed(
  seed: Uint8Array,
  accountXid?: string,
): Promise<IdentityKeyMaterial> {
  const material = await deriveIdentityKey(seed);
  if (accountXid !== undefined) {
    const normalizedAccountXid = accountXid.trim();
    if (normalizedAccountXid.length === 0) {
      throw new Error("Sign-in did not return a valid Account ID.");
    }
    if (isNativeVault() && await devicePhraseForAccount(normalizedAccountXid)) {
      const old = createWebKeyStore();
      await old.delete(normalizedAccountXid); await old.delete(`${normalizedAccountXid}:ecdh`);
      cachedAccountXid = normalizedAccountXid; cached = material; return material;
    }
    const store = createWebKeyStore();
    await store.put(normalizedAccountXid, {
      privateKey: material.privateKey,
      publicKey: material.publicKey,
    });
    await store.put(`${normalizedAccountXid}:ecdh`, material.ecdhPrivateKey);

    // Do not let the auth flow reload until both non-extractable keys
    // can be read back from IndexedDB. A successful session without its
    // local keys cannot encrypt or decrypt DMs after the reload.
    const [persistedSigningKey, persistedEcdhKey] = await Promise.all([
      store.get(normalizedAccountXid),
      store.get(`${normalizedAccountXid}:ecdh`),
    ]);
    if (persistedSigningKey === null || persistedEcdhKey === null) {
      throw new Error("Link could not save your identity key on this device.");
    }
    cachedAccountXid = normalizedAccountXid;
  } else {
    cachedAccountXid = null;
  }
  cached = material;
  return material;
}

/**
 * Build the short-lived proof required by every operator request.
 *
 * The browser signs a new random challenge with the same private key that
 * established the account session. The server checks both the signature and
 * the 60-second timestamp window, so possessing a bearer token by itself is
 * not enough to use the moderation API.
 */
export async function createFreshSignature(
  accountXid: string,
): Promise<{ signatureB64: string; challengeB64: string }> {
  const identity = await loadIdentity(accountXid);
  if (identity === null) {
    throw new Error(
      "Your local identity key is not available. Restore this operator account on this device and sign in again.",
    );
  }

  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const sessionId = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const serverTimeMs = Date.now();
  const signature = await signChallenge(identity.privateKey, {
    nonce,
    timestamp: BigInt(serverTimeMs),
    sessionId,
  });
  const challenge = {
    nonceB64: bytesToBase64(nonce),
    serverTimeMs,
    sessionId: bytesToBase64Url(sessionId),
  };

  return {
    signatureB64: bytesToBase64Url(signature),
    challengeB64: bytesToBase64Url(
      new TextEncoder().encode(JSON.stringify(challenge)),
    ),
  };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  return bytesToBase64(bytes)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}
